import React, { useEffect, useState } from "react";
import { useApp, lineTotal } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2,
  Copy,
  PackageCheck,
  MapPin,
  AlertCircle,
  UserCheck,
  Instagram,
  Gift,
} from "lucide-react";
import ProfileDialog, { formatAddress } from "./ProfileDialog";
import InstagramDiscountDialog from "./InstagramDiscountDialog";

export default function CheckoutDialog({ open, onOpenChange, onSuccess }) {
  const {
    t,
    cart,
    subtotal,
    deliveryFee,
    total,
    clearCart,
    profile,
    setProfile,
    settings,
    minOrder,
    meetsMin,
    storeStatus,
  } = useApp();
  const [payment, setPayment] = useState("cash");
  const [notes, setNotes] = useState("");
  const [gps, setGps] = useState(null); // {lat, lng, link}
  const [submitting, setSubmitting] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [igOpen, setIgOpen] = useState(false);
  const [discountStatus, setDiscountStatus] = useState(null);

  useEffect(() => {
    if (open && !profile) setProfileOpen(true);
  }, [open, profile]);

  // Fetch discount status whenever the checkout dialog opens for a known customer
  useEffect(() => {
    if (!open || !profile?.customer_number || !profile?.phone) {
      setDiscountStatus(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(
          `/customers/${profile.customer_number}/discount-status`,
          { params: { phone: profile.phone } }
        );
        setDiscountStatus(data);
      } catch {
        setDiscountStatus(null);
      }
    })();
  }, [open, profile?.customer_number, profile?.phone, igOpen]);

  // Reset confirmation when profile changes
  useEffect(() => {
    setAddressConfirmed(false);
  }, [profile?.customer_number, profile?.street, profile?.building]);

  // Local discount preview (server is source of truth on submit)
  const discountApplicable =
    !!discountStatus?.credit_available && subtotal >= 200;
  const previewDiscount = discountApplicable ? +(subtotal * 0.1).toFixed(2) : 0;
  const previewTotal = +(subtotal + deliveryFee - previewDiscount).toFixed(2);

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("location_error"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setGps({
          lat: latitude,
          lng: longitude,
          link: `https://maps.google.com/?q=${latitude},${longitude}`,
        });
        toast.success(t("location_sent"));
      },
      () => toast.error(t("location_error")),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const fullAddress = profile ? formatAddress(profile) : "";
  const isReturning = !!profile?.customer_number;

  // Load the customer's addresses when checkout opens for a known customer
  const [addresses, setAddresses] = useState([]);
  useEffect(() => {
    if (!open || !profile?.customer_number || !profile?.phone) {
      setAddresses([]);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/customers/${profile.customer_number}/addresses`, {
          params: { phone: profile.phone },
        });
        setAddresses(r.data?.addresses || []);
      } catch {
        setAddresses([]);
      }
    })();
  }, [open, profile?.customer_number, profile?.phone]);

  const pickAddress = (a) => {
    setProfile({
      ...profile,
      address_id: a.id,
      address_label: a.label || "",
      neighborhood: a.neighborhood || "",
      street: a.street || "",
      building: a.building || "",
      floor: a.floor || "",
      apt: a.apt || "",
    });
    setAddressConfirmed(true);
  };

  const doSubmit = async (profileToUse) => {
    const p = profileToUse || profile;
    if (!p) {
      setPendingSubmit(true);
      setProfileOpen(true);
      return;
    }
    if (isReturning && !addressConfirmed) {
      toast.error(t("please_confirm_address"));
      return;
    }
    if (!cart.length) return;
    setSubmitting(true);
    try {
      const items = cart.map((i) => ({
        product_id: i.id,
        name: i.name,
        price: i.price,
        quantity: i.sell_by === "kg" ? 1 : i.quantity,
        sell_by: i.sell_by || "unit",
        grams: i.sell_by === "kg" ? Number(i.grams) : null,
      }));
      const addressStr = formatAddress(p);
      const { data } = await api.post("/orders", {
        customer_name: `${p.first_name} ${p.last_name}`,
        phone: p.phone,
        address: addressStr,
        notes,
        payment_method: payment,
        items,
        gps_link: gps?.link || null,
        gps_lat: gps?.lat || null,
        gps_lng: gps?.lng || null,
        customer_number: p.customer_number || null,
        save_address: true,
        address_parts: {
          neighborhood: p.neighborhood,
          street: p.street,
          building: p.building,
          floor: p.floor,
          apt: p.apt,
        },
      });

      // Persist customer_number back into local profile so next visit skips lookup.
      if (data.customer_number && data.customer_number !== p.customer_number) {
        setProfile({ ...p, customer_number: data.customer_number });
      }

      const lines = [];
      lines.push(`[İKİZLER MARKET SİPARİŞ]`);
      lines.push(`Sipariş No: *${data.order_number}*`);
      if (data.customer_number) {
        lines.push(
          `Müşteri No: *#${String(data.customer_number).padStart(4, "0")}*`
        );
      }
      lines.push(`Ad Soyad: ${data.customer_name}`);
      lines.push(`Telefon: ${data.phone}`);
      lines.push(`Adres: ${data.address}`);
      if (data.gps_link) lines.push(`Konum: ${data.gps_link}`);
      lines.push(
        `Ödeme: ${payment === "cash" ? "Kapıda Nakit" : "Kapıda Kredi Kartı"}`
      );
      lines.push(``);
      lines.push(`*Ürünler:*`);
      cart.forEach((i) => {
        const lt = lineTotal(i);
        const amount =
          i.sell_by === "kg" ? `${i.grams} gr` : `${i.quantity} x`;
        lines.push(`• ${amount} ${i.name} — ₺${lt.toFixed(2)}`);
      });
      if (notes) lines.push(`\nNot: ${notes}`);
      lines.push(``);
      lines.push(`Ara Toplam: ₺${data.subtotal.toFixed(2)}`);
      if (data.delivery_fee > 0) {
        lines.push(`Servis Ücreti: ₺${data.delivery_fee.toFixed(2)}`);
      } else {
        lines.push(`Servis: Ücretsiz 🎁`);
      }
      lines.push(`*TOPLAM: ₺${data.total.toFixed(2)}*`);

      const waNumber = settings?.whatsapp_number || "905391095756";
      const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(
        lines.join("\n")
      )}`;

      setSuccessOrder({ ...data, wa_url: waUrl });
      window.open(waUrl, "_blank", "noopener");
      clearCart();
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sipariş oluşturulamadı");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = () => doSubmit();

  const copyOrderNo = () => {
    if (!successOrder) return;
    navigator.clipboard.writeText(successOrder.order_number);
    toast.success(t("copied"));
  };

  const close = () => {
    setSuccessOrder(null);
    setNotes("");
    setGps(null);
    setAddressConfirmed(false);
    onOpenChange(false);
  };

  if (successOrder) {
    return (
      <Dialog open={open} onOpenChange={close}>
        <DialogContent
          data-testid="order-success-dialog"
          className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md"
        >
          <div className="flex flex-col items-center text-center py-4 gap-4">
            <div className="w-16 h-16 rounded-full bg-[#2E5A36] flex items-center justify-center">
              <PackageCheck className="w-8 h-8 text-[#FAF7F2]" />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-extrabold text-[#1B2A22]">
                {t("order_success")}
              </h3>
              <p className="text-sm text-[#1B2A22]/70 mt-1">
                {t("order_sent_wa")}
              </p>
            </div>
            <div className="w-full bg-white border border-[#E8E2D5] rounded-xl p-4 flex items-center justify-between">
              <div className="text-left">
                <span className="text-[10px] uppercase tracking-wider text-[#1B2A22]/60 font-semibold">
                  {t("order_number")}
                </span>
                <p
                  data-testid="success-order-number"
                  className="font-mono font-extrabold text-[#2E5A36] text-2xl"
                >
                  #{successOrder.order_number}
                </p>
              </div>
              <Button
                data-testid="copy-order-number"
                onClick={copyOrderNo}
                variant="outline"
                size="sm"
                className="border-[#E8E2D5]"
              >
                <Copy className="w-4 h-4 mr-1" /> {t("copy")}
              </Button>
            </div>

            {successOrder.customer_number && (
              <div
                data-testid="success-customer-number"
                className="w-full bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-xl p-4 text-left"
              >
                <p className="text-[10px] uppercase tracking-wider text-[#B4711C] font-semibold">
                  {t("customer_number_label")}
                </p>
                <p className="font-mono font-extrabold text-[#B4711C] text-2xl">
                  #{String(successOrder.customer_number).padStart(4, "0")}
                </p>
                <p className="text-xs text-[#1B2A22]/70 mt-1">
                  {t("customer_number_note")}
                </p>
              </div>
            )}

            <a
              data-testid="open-whatsapp-btn"
              href={successOrder.wa_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-heading font-bold">
                📱 {t("open_whatsapp")}
              </Button>
            </a>
            <Button
              data-testid="close-success-dialog"
              onClick={close}
              variant="outline"
              className="w-full rounded-full h-11"
            >
              {t("close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="checkout-dialog"
          className="bg-[#FAF7F2] border-[#E8E2D5] max-w-lg max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {t("checkout_title")}
            </DialogTitle>
            <DialogDescription>
              {t("free_delivery_note_dynamic", { min: minOrder.toFixed(0) })}
            </DialogDescription>
          </DialogHeader>

          {settings && !storeStatus.storeOpen && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {t("store_closed_desc", {
                  open: settings.store_open_hour,
                  close: settings.store_close_hour,
                })}
              </span>
            </div>
          )}
          {settings && storeStatus.storeOpen && !storeStatus.deliveryOpen && (
            <div className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-xl p-3 text-sm text-[#B4711C] flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {t("delivery_closed_desc", {
                  start: settings.delivery_start_hour,
                  end: settings.delivery_end_hour,
                })}
              </span>
            </div>
          )}

          {!meetsMin && (
            <div
              data-testid="service-fee-notice"
              className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-xl p-3 text-sm text-[#B4711C] flex gap-2 items-start"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {t("service_fee_notice", {
                  fee: deliveryFee.toFixed(2),
                  remaining: Math.max(0, minOrder - subtotal).toFixed(2),
                  min: minOrder.toFixed(0),
                })}
              </span>
            </div>
          )}

          {false && (
            <div data-testid="min-order-warning" className="hidden" />
          )}

          {/* Profile summary */}
          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4">
            {profile ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {profile.customer_number && (
                      <div className="inline-flex items-center gap-1 bg-[#2E5A36]/10 rounded-full px-2 py-0.5 mb-2">
                        <UserCheck className="w-3 h-3 text-[#2E5A36]" />
                        <span className="text-[10px] font-heading font-bold text-[#2E5A36]">
                          {t("customer_number_label")} #
                          {String(profile.customer_number).padStart(4, "0")}
                        </span>
                      </div>
                    )}
                    <p className="font-heading font-bold text-[#1B2A22]">
                      {profile.first_name} {profile.last_name}
                    </p>
                    <p className="text-xs text-[#1B2A22]/70 mt-1">{profile.phone}</p>
                    {profile.address_label && (
                      <span className="inline-block mt-1 text-[10px] font-heading font-bold text-[#B4711C] bg-[#E5A024]/15 rounded-full px-2 py-0.5">
                        📍 {profile.address_label}
                      </span>
                    )}
                    <p className="text-xs text-[#1B2A22]/70 mt-1 break-words">
                      {fullAddress}
                    </p>
                  </div>
                  <Button
                    data-testid="edit-profile-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => setProfileOpen(true)}
                    className="rounded-full"
                  >
                    {t("edit")}
                  </Button>
                </div>

                {/* Multi-address quick switcher (returning customer with >=2 saved addresses) */}
                {isReturning && addresses.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-[#E8E2D5]" data-testid="checkout-addr-switcher">
                    <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-2">
                      Teslimat Adresi Seç
                    </p>
                    <div className="flex overflow-x-auto no-scrollbar gap-2 -mx-1 px-1">
                      {addresses.map((a) => {
                        const active = profile.address_id === a.id;
                        return (
                          <button
                            key={a.id}
                            type="button"
                            data-testid={`checkout-addr-${a.id}`}
                            onClick={() => pickAddress(a)}
                            className={`shrink-0 text-left rounded-xl px-3 py-2 border transition min-w-[140px] max-w-[220px] ${
                              active
                                ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]"
                                : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
                            }`}
                          >
                            <div className="flex items-center gap-1 text-xs font-heading font-bold">
                              📍 {a.label || "Adres"}
                              {a.is_default && <span className="text-[9px] opacity-70">★</span>}
                            </div>
                            <p className={`text-[10px] mt-1 line-clamp-2 ${active ? "opacity-90" : "opacity-70"}`}>
                              {a.formatted || "—"}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Address confirmation — mandatory for returning customers */}
                {isReturning && (
                  <label
                    data-testid="confirm-address-checkbox"
                    className={`mt-3 flex items-start gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                      addressConfirmed
                        ? "bg-[#2E5A36]/10 border-[#2E5A36]"
                        : "bg-[#E5A024]/10 border-[#E5A024]/50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={addressConfirmed}
                      onChange={(e) => setAddressConfirmed(e.target.checked)}
                    />
                    <div>
                      <p className="text-sm font-heading font-bold text-[#1B2A22]">
                        {t("confirm_address_title")}
                      </p>
                      <p className="text-xs text-[#1B2A22]/70">
                        {t("confirm_address_desc")}
                      </p>
                    </div>
                  </label>
                )}
              </>
            ) : (
              <Button
                data-testid="open-profile-btn"
                onClick={() => setProfileOpen(true)}
                className="w-full bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
              >
                {t("profile_title")} — {t("edit")}
              </Button>
            )}
          </div>

          <Button
            data-testid="send-location-btn"
            onClick={requestLocation}
            variant="outline"
            className={`w-full h-12 border-[#E8E2D5] rounded-xl font-semibold ${
              gps
                ? "bg-[#2E5A36]/10 text-[#2E5A36] border-[#2E5A36]"
                : "bg-white"
            }`}
          >
            <MapPin className="w-4 h-4 mr-2" />
            {gps ? t("location_sent") : t("send_location")}
          </Button>

          <div>
            <Label className="text-xs font-heading font-bold uppercase tracking-wider">
              {t("order_notes")}
            </Label>
            <Textarea
              data-testid="checkout-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 bg-white border-[#E8E2D5]"
            />
          </div>

          <div>
            <Label className="text-xs font-heading font-bold uppercase tracking-wider mb-2 block">
              {t("payment_method")}
            </Label>
            <RadioGroup
              value={payment}
              onValueChange={setPayment}
              className="grid grid-cols-2 gap-2"
            >
              {[
                { v: "cash", l: t("cash") },
                { v: "card_on_delivery", l: t("card_on_delivery") },
              ].map((opt) => (
                <label
                  key={opt.v}
                  data-testid={`pay-${opt.v}`}
                  className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${
                    payment === opt.v
                      ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]"
                      : "bg-white border-[#E8E2D5]"
                  }`}
                >
                  <RadioGroupItem value={opt.v} className="sr-only" />
                  <CheckCircle2
                    className={`w-4 h-4 ${
                      payment === opt.v ? "opacity-100" : "opacity-30"
                    }`}
                  />
                  <span className="text-sm font-semibold">{opt.l}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#1B2A22]/70">{t("subtotal")}</span>
              <span className="font-mono font-semibold">
                ₺{subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#1B2A22]/70">{t("delivery_fee")}</span>
              <span
                data-testid="checkout-delivery-fee"
                className={`font-mono font-semibold ${
                  deliveryFee > 0 ? "text-[#D9653B]" : "text-[#2E5A36]"
                }`}
              >
                {deliveryFee > 0 ? `₺${deliveryFee.toFixed(2)}` : t("free")}
              </span>
            </div>
            {discountApplicable && (
              <div
                data-testid="checkout-ig-discount"
                className="flex justify-between text-sm bg-[#2E5A36]/5 -mx-4 px-4 py-2"
              >
                <span className="text-[#2E5A36] flex items-center gap-1 font-semibold">
                  <Instagram className="w-3.5 h-3.5" /> {t("ig_discount_line")}
                </span>
                <span className="font-mono font-bold text-[#2E5A36]">
                  −₺{previewDiscount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-[#E8E2D5]">
              <span className="font-heading font-bold">{t("total")}</span>
              <span className="font-mono font-extrabold text-[#2E5A36] text-lg">
                ₺{previewTotal.toFixed(2)}
              </span>
            </div>
          </div>

          <Button
            data-testid="checkout-submit-btn"
            onClick={submit}
            disabled={
              submitting ||
              cart.length === 0 ||
              !storeStatus.storeOpen ||
              (isReturning && !addressConfirmed)
            }
            className="w-full h-12 bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold rounded-full disabled:opacity-50"
          >
            {submitting ? "..." : `📱 ${t("place_order")}`}
          </Button>

          {/* Instagram discount block */}
          {profile?.customer_number && (
            <div
              data-testid="ig-discount-block"
              className="mt-1 bg-white border border-[#E8E2D5] rounded-xl p-3"
            >
              {discountStatus?.used ? (
                <div className="flex items-center gap-2 text-xs text-[#1B2A22]/60">
                  <CheckCircle2 className="w-4 h-4 text-[#2E5A36]" />
                  {t("ig_status_used")}
                </div>
              ) : discountStatus?.credit_available ? (
                <div className="flex items-center gap-2 text-sm">
                  <Gift className="w-4 h-4 text-[#2E5A36]" />
                  <span className="font-heading font-bold text-[#2E5A36]">
                    {discountApplicable
                      ? t("ig_status_applied")
                      : t("ig_status_ready")}
                  </span>
                </div>
              ) : discountStatus?.has_pending ? (
                <div className="flex items-center gap-2 text-xs text-[#B4711C]">
                  <AlertCircle className="w-4 h-4" />
                  {t("ig_status_pending")}
                </div>
              ) : (
                <button
                  data-testid="open-ig-discount-btn"
                  onClick={() => setIgOpen(true)}
                  className="w-full flex items-center gap-3 text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] flex items-center justify-center shrink-0">
                    <Instagram className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading font-bold text-sm text-[#1B2A22]">
                      {t("ig_cta_title")}
                    </p>
                    <p className="text-[11px] text-[#1B2A22]/60">
                      {t("ig_cta_desc")}
                    </p>
                  </div>
                  <span className="text-xs font-heading font-bold text-[#DD2A7B]">
                    →
                  </span>
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onSaved={(p) => {
          if (pendingSubmit) {
            setPendingSubmit(false);
            doSubmit(p);
          }
        }}
      />

      <InstagramDiscountDialog
        open={igOpen}
        onOpenChange={setIgOpen}
        profile={profile}
      />
    </>
  );
}
